import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

const translations = {
    en: {
        // Navigation
        privateAuction: 'Auction',
        gallery: 'Gallery',
        scan: 'Add',
        myYess: 'My Yess',
        about: 'About',
        home: 'Home',
        menu: 'Menu',
        profile: 'Profile',
        news: 'News',
        
        // Auth
        welcome: 'Welcome to',
        phoneNumber: 'Phone Number',
        enterPhone: 'Enter your phone number',
        sendCode: 'Send Code',
        verifyCode: 'Verify Code',
        enterCode: 'Enter the code sent to your phone',
        yourName: 'Your Name',
        firstName: 'First Name',
        lastName: 'Last Name',
        inviteCode: 'Invite Code',
        inviteCodeOptional: 'Invite Code (if you have one)',
        login: 'Sign In',
        logout: 'Log Out',
        logOut: 'Log Out',
        demoOtp: 'Demo OTP: 123456',
        
        // Hero
        heroSlogan: 'Women. Worth. Second Lives.',
        heroSubtext: 'A curated charity auction salon',
        
        // Auction/Events
        upcomingAuctions: 'Upcoming Auctions',
        auctionEvents: 'Auction Events',
        liveNow: 'Live Now',
        archived: 'Archived',
        upcoming: 'Upcoming',
        past: 'Past',
        getAccess: 'Request Access',
        enterAuction: 'Enter Auction',
        accessGranted: 'Access Granted',
        auctionDetails: 'Auction Details',
        location: 'Location',
        dateTime: 'Date & Time',
        program: 'Program',
        capacity: 'Capacity',
        spotsLeft: 'spots left',
        soldOut: 'Fully Booked',
        noUpcomingEvents: 'No upcoming events',
        noPastEvents: 'No past events',
        getTicket: 'Get Ticket',
        ticketPurchased: 'Ticket Purchased',
        
        // Gallery / Items
        yessItems: 'Yess Items',
        allItems: 'All Items',
        filters: 'Filters',
        category: 'Category',
        size: 'Size',
        price: 'Starting Bid',
        listingType: 'Type',
        coat: 'Coat',
        dress: 'Dress',
        top: 'Top',
        pants: 'Pants',
        shoes: 'Shoes',
        accessories: 'Accessories',
        other: 'Other',
        auction: 'Auction',
        clearFilters: 'Clear Filters',
        noItems: 'No items in this auction yet',
        noItemsYet: 'No items yet',
        
        // Item actions
        yess: 'Yess',
        hold: 'Reserve',
        placeBid: 'Place Bid',
        startingBid: 'Starting Bid',
        currentBid: 'Current Bid',
        onHold: 'Reserved',
        sold: 'Sold',
        holdExpires: 'Reservation expires in',
        releaseHold: 'Release',
        
        // Scan/Add
        addItem: 'Add Auction Item',
        scanLabel: 'Scan Label',
        recognizeItem: 'Recognize Item',
        uploadPhoto: 'Upload Photo',
        takePhoto: 'Take Photo',
        itemTitle: 'Title',
        itemCategory: 'Category',
        itemSize: 'Size',
        itemMaterials: 'Materials',
        itemCare: 'Care Instructions',
        itemBrand: 'Brand',
        itemStartingBid: 'Starting Bid (€)',
        publishItem: 'Submit for Auction',
        processing: 'Processing...',
        maxItemsReached: 'Maximum 5 auction items per person',
        
        // My Yess
        myItems: 'My Auction Items',
        myReactions: 'My Favorites',
        myHolds: 'My Reservations',
        myOrders: 'Won Auctions',
        noOrders: 'No won auctions yet',
        pickupAt: 'Collect at auction',
        
        // Profile
        personalDetails: 'Personal Details',
        personalQR: 'Personal QR Code',
        language: 'Language',
        swapCredits: 'Swap Credits',
        remaining: 'remaining',
        notCheckedIn: 'Not checked in yet',
        swapCreditsInfo: 'Your swap credits will appear here after check-in at the event',
        swapCreditsUse: 'Use your swap credits to take home items from the swap table or racks',
        deleteAccount: 'Delete Account',
        deleteAccountConfirm: 'This action cannot be undone. This will permanently delete your account and remove all your data.',
        
        // History
        history: 'History',
        noHistory: 'No event history yet',
        ticketsPurchased: 'Tickets purchased',
        donationMade: 'Donation made',
        
        // About
        aboutUs: 'About Us',
        theBrains: 'The Curators',
        ourMission: 'Our Mission',
        missionText: 'Women own beautiful clothes — not out of excess, but care. Galeries Yess gives these cherished garments a second life through curated charity auctions, creating connection and turning letting go into something meaningful.',
        
        // House Rules
        houseRules: 'House Rules',
        houseRulesSubtitle: 'Guidelines for our salon',
        twoSystems: 'Two Separate Systems',
        twoSystemsText: 'Galeries Yess operates two distinct systems: Auction items (digital, uploaded in the app) and Swap items (physical, brought to the event). These are completely separate.',
        auctionItemsTitle: 'Auction Items',
        auctionItemsText: 'You may upload a maximum of 5 auction items per event. Auction items are displayed in the app and auctioned during the physical event.',
        auctionItemsNote: 'If an auction item receives no bids, you are responsible for taking it home. Galeries Yess is not responsible for unsold items.',
        swapItemsTitle: 'Swap Items',
        swapItemsText: 'You may bring a maximum of 25 swap items per event. Swap items are physical donations brought to the venue — they are not uploaded to the app.',
        swapCreditsText: 'At check-in, your swap items are converted into swap credits, which you can use to take home items from the swap table during the event.',
        swapItemsNote: 'All swap items are donated and cannot be reclaimed. Any unused swap credits remain in your profile and are valid for future events.',
        auctionEtiquette: 'Auction Etiquette',
        auctionEtiquetteText: 'Bids are binding. Please only bid on items you genuinely wish to acquire. All purchases must be collected at the physical event.',
        eventConduct: 'Event Conduct',
        eventConductText: 'Our salon events are intimate gatherings. Please arrive on time, dress comfortably, and embrace the spirit of community and sustainability.',
        exclusivity: 'Exclusivity',
        exclusivityText: 'Galeries Yess is an invite-only salon. Access codes are personal and non-transferable. Please respect the privacy of our community.',
        thankYou: 'Thank you for being part of Galeries Yess',
        
        // Admin
        adminPanel: 'Admin Panel',
        createAuction: 'Create Auction',
        manageInvites: 'Manage Invites',
        generateInvites: 'Generate Invites',
        statistics: 'Statistics',
        totalUsers: 'Total Guests',
        totalItems: 'Total Items',
        itemsSold: 'Items Sold',
        totalRevenue: 'Total Raised',
        guestList: 'Guest List',
        addGuest: 'Add Guest',
        registered: 'Registered',
        invited: 'Invited',
        
        // Common
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        cancel: 'Cancel',
        save: 'Save',
        saveChanges: 'Save Changes',
        delete: 'Delete',
        edit: 'Edit',
        confirm: 'Confirm',
        back: 'Back',
        next: 'Next',
        submit: 'Submit',
        search: 'Search',
        close: 'Close',
        view: 'View',
        viewAll: 'View All',
        event: 'Event',
        charity: 'Charity',
        charitySubtitle: 'Making a difference together',
    },
    nl: {
        // Navigation
        privateAuction: 'Veiling',
        gallery: 'Galerij',
        scan: 'Toevoegen',
        myYess: 'Mijn Yess',
        about: 'Over Ons',
        home: 'Home',
        menu: 'Menu',
        profile: 'Profiel',
        news: 'Nieuws',
        
        // Auth
        welcome: 'Welkom bij',
        phoneNumber: 'Telefoonnummer',
        enterPhone: 'Voer je telefoonnummer in',
        sendCode: 'Verstuur Code',
        verifyCode: 'Verifieer Code',
        enterCode: 'Voer de code in die naar je telefoon is gestuurd',
        yourName: 'Je Naam',
        firstName: 'Voornaam',
        lastName: 'Achternaam',
        inviteCode: 'Uitnodigingscode',
        inviteCodeOptional: 'Uitnodigingscode (indien je er een hebt)',
        login: 'Inloggen',
        logout: 'Uitloggen',
        logOut: 'Uitloggen',
        demoOtp: 'Demo OTP: 123456',
        
        // Hero
        heroSlogan: 'Vrouwen. Waarde. Tweede Levens.',
        heroSubtext: 'Een gecureerde liefdadigheidsveiling',
        
        // Auction/Events
        upcomingAuctions: 'Aankomende Veilingen',
        auctionEvents: 'Veiling Evenementen',
        liveNow: 'Nu Live',
        archived: 'Archief',
        upcoming: 'Aankomend',
        past: 'Voorbij',
        getAccess: 'Toegang Aanvragen',
        enterAuction: 'Naar Veiling',
        accessGranted: 'Toegang Verleend',
        auctionDetails: 'Veiling Details',
        location: 'Locatie',
        dateTime: 'Datum & Tijd',
        program: 'Programma',
        capacity: 'Capaciteit',
        spotsLeft: 'plekken over',
        soldOut: 'Volgeboekt',
        noUpcomingEvents: 'Geen aankomende evenementen',
        noPastEvents: 'Geen afgelopen evenementen',
        getTicket: 'Ticket Kopen',
        ticketPurchased: 'Ticket Gekocht',
        
        // Gallery / Items
        yessItems: 'Yess Items',
        allItems: 'Alle Items',
        filters: 'Filters',
        category: 'Categorie',
        size: 'Maat',
        price: 'Startbod',
        listingType: 'Type',
        coat: 'Jas',
        dress: 'Jurk',
        top: 'Top',
        pants: 'Broek',
        shoes: 'Schoenen',
        accessories: 'Accessoires',
        other: 'Overig',
        auction: 'Veiling',
        clearFilters: 'Filters Wissen',
        noItems: 'Nog geen items in deze veiling',
        noItemsYet: 'Nog geen items',
        
        // Item actions
        yess: 'Yess',
        hold: 'Reserveer',
        placeBid: 'Bied',
        startingBid: 'Startbod',
        currentBid: 'Huidig Bod',
        onHold: 'Gereserveerd',
        sold: 'Verkocht',
        holdExpires: 'Reservering verloopt over',
        releaseHold: 'Loslaten',
        
        // Scan/Add
        addItem: 'Veiling Item Toevoegen',
        scanLabel: 'Scan Label',
        recognizeItem: 'Herken Item',
        uploadPhoto: 'Upload Foto',
        takePhoto: 'Maak Foto',
        itemTitle: 'Titel',
        itemCategory: 'Categorie',
        itemSize: 'Maat',
        itemMaterials: 'Materialen',
        itemCare: 'Wasinstructies',
        itemBrand: 'Merk',
        itemStartingBid: 'Startbod (€)',
        publishItem: 'Indienen voor Veiling',
        processing: 'Verwerken...',
        maxItemsReached: 'Maximaal 5 veiling items per persoon',
        
        // My Yess
        myItems: 'Mijn Veiling Items',
        myReactions: 'Mijn Favorieten',
        myHolds: 'Mijn Reserveringen',
        myOrders: 'Gewonnen Veilingen',
        noOrders: 'Nog geen gewonnen veilingen',
        pickupAt: 'Ophalen bij veiling',
        
        // Profile
        personalDetails: 'Persoonlijke Gegevens',
        personalQR: 'Persoonlijke QR Code',
        language: 'Taal',
        swapCredits: 'Ruil Tegoed',
        remaining: 'over',
        notCheckedIn: 'Nog niet ingecheckt',
        swapCreditsInfo: 'Je ruil tegoed verschijnt hier na het inchecken bij het evenement',
        swapCreditsUse: 'Gebruik je ruil tegoed om items van de ruiltafel of rekken mee te nemen',
        deleteAccount: 'Account Verwijderen',
        deleteAccountConfirm: 'Deze actie kan niet ongedaan worden gemaakt. Dit verwijdert permanent je account en al je gegevens.',
        
        // History
        history: 'Geschiedenis',
        noHistory: 'Nog geen evenement geschiedenis',
        ticketsPurchased: 'Tickets gekocht',
        donationMade: 'Donatie gedaan',
        
        // About
        aboutUs: 'Over Ons',
        theBrains: 'De Curatoren',
        ourMission: 'Onze Missie',
        missionText: 'Vrouwen bezitten prachtige kleding — niet uit overdaad, maar uit zorg. Galeries Yess geeft deze gekoesterde kledingstukken een tweede leven via gecureerde liefdadigheidsveilingen, creëert verbinding en maakt loslaten tot iets betekenisvols.',
        
        // House Rules
        houseRules: 'Huisregels',
        houseRulesSubtitle: 'Richtlijnen voor onze salon',
        twoSystems: 'Twee Aparte Systemen',
        twoSystemsText: 'Galeries Yess werkt met twee aparte systemen: Veiling items (digitaal, geüpload in de app) en Ruil items (fysiek, meegebracht naar het evenement). Deze zijn volledig gescheiden.',
        auctionItemsTitle: 'Veiling Items',
        auctionItemsText: 'Je mag maximaal 5 veiling items per evenement uploaden. Veiling items worden getoond in de app en geveild tijdens het fysieke evenement.',
        auctionItemsNote: 'Als een veiling item geen biedingen krijgt, ben je zelf verantwoordelijk voor het meenemen. Galeries Yess is niet verantwoordelijk voor onverkochte items.',
        swapItemsTitle: 'Ruil Items',
        swapItemsText: 'Je mag maximaal 25 ruil items per evenement meebrengen. Ruil items zijn fysieke donaties die naar de locatie worden gebracht — ze worden niet geüpload naar de app.',
        swapCreditsText: 'Bij het inchecken worden je ruil items omgezet in ruil tegoed, dat je kunt gebruiken om items van de ruiltafel mee te nemen tijdens het evenement.',
        swapItemsNote: 'Alle ruil items zijn gedoneerd en kunnen niet worden teruggevorderd. Ongebruikt ruil tegoed blijft in je profiel en is geldig voor toekomstige evenementen.',
        auctionEtiquette: 'Veiling Etiquette',
        auctionEtiquetteText: 'Biedingen zijn bindend. Bied alleen op items die je echt wilt hebben. Alle aankopen moeten worden opgehaald bij het fysieke evenement.',
        eventConduct: 'Evenement Gedrag',
        eventConductText: 'Onze salon evenementen zijn intieme bijeenkomsten. Kom alsjeblieft op tijd, kleed je comfortabel en omarm de geest van gemeenschap en duurzaamheid.',
        exclusivity: 'Exclusiviteit',
        exclusivityText: 'Galeries Yess is een salon op uitnodiging. Toegangscodes zijn persoonlijk en niet overdraagbaar. Respecteer alsjeblieft de privacy van onze gemeenschap.',
        thankYou: 'Bedankt dat je deel uitmaakt van Galeries Yess',
        
        // Admin
        adminPanel: 'Admin Paneel',
        createAuction: 'Maak Veiling',
        manageInvites: 'Beheer Uitnodigingen',
        generateInvites: 'Genereer Uitnodigingen',
        statistics: 'Statistieken',
        totalUsers: 'Totaal Gasten',
        totalItems: 'Totaal Items',
        itemsSold: 'Items Verkocht',
        totalRevenue: 'Totaal Opgehaald',
        guestList: 'Gastenlijst',
        addGuest: 'Gast Toevoegen',
        registered: 'Geregistreerd',
        invited: 'Uitgenodigd',
        
        // Common
        loading: 'Laden...',
        error: 'Fout',
        success: 'Gelukt',
        cancel: 'Annuleren',
        save: 'Opslaan',
        saveChanges: 'Wijzigingen Opslaan',
        delete: 'Verwijderen',
        edit: 'Bewerken',
        confirm: 'Bevestigen',
        back: 'Terug',
        next: 'Volgende',
        submit: 'Versturen',
        search: 'Zoeken',
        close: 'Sluiten',
        view: 'Bekijken',
        viewAll: 'Alles Bekijken',
        event: 'Evenement',
        charity: 'Goed Doel',
        charitySubtitle: 'Samen het verschil maken',
    },
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        return localStorage.getItem('galeries_language') || 'en';
    });

    useEffect(() => {
        localStorage.setItem('galeries_language', language);
    }, [language]);

    const t = (key) => {
        return translations[language][key] || key;
    };

    const toggleLanguage = () => {
        setLanguage((prev) => (prev === 'en' ? 'nl' : 'en'));
    };

    const value = {
        language,
        setLanguage,
        toggleLanguage,
        t,
        isEnglish: language === 'en',
        isDutch: language === 'nl',
    };

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export default LanguageContext;
